import React, { Component, Fragment } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { selectCardinalPlural } from '~/utils/Intl';
import ProductGroupDownloadPopover from '~/ProductGroupDownloadPopover';
import ProductGroupOptionsPopover from '~/ProductGroupOptionsPopover';
import Button, { Link } from '~/UI/Button';
import iconButtonStyles from '~/UI/Button/icon.less';
import primaryIconButtonStyles from '~/UI/Button/primary-icon.less';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Heading from '~/UI/Heading/Heading';
import Icon from '~/UI/Icon/Icon';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';

import styles from './styles.less';

interface Props {
	productGroup: UserProductGroup;
	currency: string | null;
	link: { pathname: string; search: string };
	showOptionsPopover: boolean;
	showDownloadPopover: boolean;
	showModule: boolean;
	isUpdatingNotification: boolean;
	actions?: React.ReactNode;
	error?: React.ReactNode;
	formatDate(date: Date, includeYear?: boolean, locale?: string[]): string;
	onNotificationsClick(): void;
	onOptionsClick(): void;
	onCloseOptionsPopover(): void;
	onCopy(): void;
	onRename(): void;
	onDelete(): void;
	onDownloadClick(): void;
	onCloseDownloadPopover(): void;
}

export default class Item extends Component<Props> {
	static defaultProps = {
		showModule: false,
	};

	optionsPopoverContainerElement: HTMLSpanElement | null = null;

	downloadPopoverContainerElement: HTMLSpanElement | null = null;

	render() {
		const {
			productGroup,
			link,
			showOptionsPopover,
			showModule,
			formatDate,
			isUpdatingNotification,
			actions = null,
			error = null,
			onNotificationsClick,
			onOptionsClick,
			onCloseOptionsPopover,
			onCopy,
			onRename,
			onDelete,
		} = this.props;

		const optionsButton = (
			<Button styles={iconButtonStyles} onClick={showOptionsPopover ? onCloseOptionsPopover : onOptionsClick}>
				<Icon name='options'>options</Icon>
			</Button>
		);

		return (
			<div className={actions !== null ? styles.expandedBox : styles.box} data-testid='group-list-item'>
				<div className={actions !== null ? styles.compactDescription : styles.description}>
					{productGroup.isProductList() ? (
						<p className={styles.tag}>LIST</p>
					) : (
						<p className={styles.tagFolder}>FOLDER</p>
					)}
					<Heading level={2} title={productGroup.name}>
						<Link styles={{ button: '' }} to={link}>
							{productGroup.name}
						</Link>
					</Heading>
					<p className={styles.moduleContent}>
						{showModule ? this.renderModule() : null}
						{productGroup.isGroup() ? (
							<Fragment>
								{productGroup.productGroupsCount}{' '}
								{selectCardinalPlural(productGroup.productGroupsCount, ['list', 'lists'])}
							</Fragment>
						) : (
							<Fragment>
								{productGroup.productsCount} {selectCardinalPlural(productGroup.productsCount, ['product', 'products'])}
							</Fragment>
						)}
					</p>
					{error !== null ? <div className={styles.error}>{error}</div> : null}
				</div>

				<div className={actions !== null ? styles.expandedActions : styles.actions}>
					{actions}
					{this.renderDownloadButton()}

					{productGroup.hasNotificationSettingsLink() ? (
						<span className={isUpdatingNotification ? styles.updatingButton : ''}>
							<Button styles={iconButtonStyles} onClick={onNotificationsClick}>
								<Icon name={productGroup.notification.settings.isDisabled() ? 'bell' : 'bell-full'}>notifications</Icon>
							</Button>
						</span>
					) : null}

					<span ref={(ref) => (this.optionsPopoverContainerElement = ref)}>
						<LayoutConstraint reference={optionsButton}>
							{showOptionsPopover ? (
								<ProductGroupOptionsPopover
									onClose={this.handleCloseOptionsPopover}
									onCopy={onCopy}
									onDelete={onDelete}
									onRename={onRename}
								/>
							) : null}
						</LayoutConstraint>
					</span>

					<p>
						Last product added/removed on <strong>{formatDate(productGroup.itemsUpdatedTime)}</strong>
					</p>
				</div>
			</div>
		);
	}

	renderModule() {
		const { productGroup } = this.props;

		if (productGroup.hasCzMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='CZ' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasCzReimbursementModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='CZ' /> Reimbursement
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasSkMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='SK' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasAeMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='AE' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasSiMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='SI' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasHrMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='HR' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasBgMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='BG' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasRoMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='RO' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}
		if (productGroup.hasLtMaxPriceModule()) {
			return (
				<Fragment>
					<strong className={styles.module}>
						<CountryIcon code='LT' /> Max Price
					</strong>
					<i className={styles.space}> • </i>
				</Fragment>
			);
		}

		return null;
	}

	renderDownloadButton() {
		const { productGroup, currency, showDownloadPopover, onDownloadClick, onCloseDownloadPopover } = this.props;

		const downloadButton = (
			<Button styles={primaryIconButtonStyles} onClick={showDownloadPopover ? onCloseDownloadPopover : onDownloadClick}>
				<Icon name='download'>download</Icon> Download
			</Button>
		);

		return (
			<span ref={(ref) => (this.downloadPopoverContainerElement = ref)}>
				<LayoutConstraint reference={downloadButton}>
					{showDownloadPopover ? (
						<ProductGroupDownloadPopover
							currency={currency}
							group={productGroup}
							onClose={this.handleCloseDownloadPopover}
						/>
					) : null}
				</LayoutConstraint>
			</span>
		);
	}

	handleCloseOptionsPopover = (event: MouseEvent) => {
		if (
			this.optionsPopoverContainerElement &&
			event.target !== this.optionsPopoverContainerElement &&
			event.target instanceof HTMLElement &&
			!this.optionsPopoverContainerElement.contains(event.target)
		) {
			this.props.onCloseOptionsPopover();
		}
	};

	handleCloseDownloadPopover = (event: MouseEvent) => {
		if (
			this.downloadPopoverContainerElement &&
			event.target !== this.downloadPopoverContainerElement &&
			event.target instanceof HTMLElement &&
			!this.downloadPopoverContainerElement.contains(event.target)
		) {
			this.props.onCloseDownloadPopover();
		}
	};
}
